import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Dot,
} from "recharts";
import {
  format,
  subWeeks,
  startOfWeek,
  addDays,
  parseISO,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import DefaultLayout from "../layouts/default/Default.layout";
import { CampaignModal } from "../features/campaigns";
import usePageTitle from "../../core/hooks/UsePageTitle";
import useResolucaoQuestoes from "../../core/hooks/UseResolucaoQuestoes";
import { FilterChipStrip } from "../components/filters";
import MateriaService from "../../sdk/services/MateriaService";
import ReviewService from "../../sdk/services/ReviewService";
import MaturityBar from "../components/MaturityBar/MaturityBar";
import { ReviewStats } from "../../sdk/@types/Revisao";
import ResolucaoService from "../../sdk/services/ResolucaoService";
import InscritoService from "../../sdk/services/InscritoService";
import { Materia } from "../../sdk/@types";
import useNextSimulado from "../../core/hooks/useNextSimulado";
import useUsuario from "../../core/hooks/UseUsuario";
import useDueCount from "../../core/hooks/useDueCount";
import useMediaQuery from "../../core/hooks/useMediaQuery";
import { Dashboard3DPanel } from "../features/dashboard3d";

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  green:   "#82ca9d",
  purple:  "#8884d8",
  greenDk: "#3d9b6e",
  greenLt: "#c6e6d4",
  heat0:   "#f1f5f9",
  heat1:   "#c6e6d4",
  heat2:   "#82ca9d",
  heat3:   "#3d9b6e",
  heat4:   "#1a6b4a",
  // foreground pair for text drawn on a heat swatch — picked by the
  // swatch's own fixed colour, not the page theme, so it's a companion to
  // the heat ramp above rather than a --c-text token.
  heatFg:            "#0f172a",
  heatFgMuted:       "#334155",
  heatFgOnDark:      "#fff",
  heatFgOnDarkMuted: "#d1fae5",
  // white ring around a line-chart data-point marker — fixed chart
  // decoration, not a themed surface.
  dotRing: "#fff",
};

// ─── Revisão (R16) ────────────────────────────────────────────────────────────
// Forecast bars: #3d9b6e is the only brand green clearing 3:1 against BOTH the
// light (#fff) and dark (#111827) card surfaces — #82ca9d (1.93) and the lime
// accent #a3e635 (1.51) both fail on white. Verified with the dataviz validator.
const FORECAST_BAR = C.greenDk;

// Recharts grid/axis/tick/cursor props take a literal colour string, not a
// Tailwind class, so they read the live custom property off the root
// element instead of a hardcoded hex. One resolver, used everywhere a chart
// needs a token colour — reads the current theme's value with no isDark
// ternary at the call site, since the browser already resolved --c-* for
// the theme that's active when this runs.
function cssVar(name: string, alpha?: number): string {
  const triplet = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return alpha === undefined ? `rgb(${triplet})` : `rgb(${triplet} / ${alpha})`;
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface TagStat   { tag: string; correct: number; wrong: number }
interface TagCount  { tag: string; count: number }
interface HeatCell { date: Date; count: number; isFuture: boolean }

interface TooltipPayload { name?: string; value?: number; color?: string; payload?: any }
interface CustomTooltipProps { active?: boolean; payload?: TooltipPayload[]; label?: string }

interface MockStat {
  name: string; fullName: string; label: string;
  correct: number; total: number; pct: number;
}

const WEEKLY_GOAL = 50;

// ── Streak helpers ────────────────────────────────────────────────────────────
function resolvedDateKeys(resolucoes: any[]): Set<string> {
  return new Set(
    resolucoes
      .map(r => { try { return format(parseISO(r.resolvedAt), "yyyy-MM-dd"); } catch { return null; } })
      .filter(Boolean) as string[]
  );
}

function calcCurrentStreak(resolucoes: any[]): number {
  const dateSet = resolvedDateKeys(resolucoes);
  const today = new Date();
  let streak = 0;
  const d = new Date(today);
  while (dateSet.has(format(d, "yyyy-MM-dd"))) { streak++; d.setDate(d.getDate() - 1); }
  return streak;
}

function calcBestStreak(resolucoes: any[]): number {
  const dates = Array.from(resolvedDateKeys(resolucoes)).sort();
  let best = 0, cur = 0;
  for (let i = 0; i < dates.length; i++) {
    if (i === 0) { cur = 1; best = 1; continue; }
    const prev = new Date(dates[i - 1]);
    const curr = new Date(dates[i]);
    const diff = (curr.getTime() - prev.getTime()) / 86_400_000;
    cur = diff === 1 ? cur + 1 : 1;
    if (cur > best) best = cur;
  }
  return best;
}

export interface WeekStripDay {
  date: Date;
  key: string;
  label: string;
  active: boolean;
  isToday: boolean;
}

// Same resolved-date set calcCurrentStreak/calcBestStreak build, so the
// streak card's two readouts and the week strip never disagree about which
// days count as answered.
export function buildWeekStrip(resolucoes: any[], today: Date = new Date()): WeekStripDay[] {
  const dateSet = resolvedDateKeys(resolucoes);
  const weekStart = startOfWeek(today, { weekStartsOn: 0 });
  const todayKey = format(today, "yyyy-MM-dd");
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(weekStart, i);
    const key = format(date, "yyyy-MM-dd");
    return {
      date,
      key,
      label: format(date, "EEE", { locale: ptBR }).charAt(0).toUpperCase(),
      active: dateSet.has(key),
      isToday: key === todayKey,
    };
  });
}

function daysUntil(dateStr?: string): { text: string; urgent: boolean } {
  if (!dateStr) return { text: "", urgent: false };
  try {
    const target = parseISO(dateStr);
    const now    = new Date();
    const targetDay = new Date(target.getFullYear(), target.getMonth(), target.getDate());
    const today     = new Date(now.getFullYear(),    now.getMonth(),    now.getDate());
    const diff = Math.round((targetDay.getTime() - today.getTime()) / 86_400_000);
    if (diff < 0)   return { text: "encerrado", urgent: false };
    if (diff === 0) return { text: "hoje",      urgent: true  };
    if (diff === 1) return { text: "amanhã",    urgent: true  };
    return { text: `em ${diff} dias`, urgent: diff <= 3 };
  } catch { return { text: "", urgent: false }; }
}

// ─── Shared tooltip ───────────────────────────────────────────────────────────
function ChartTip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const ac = payload.find(p => p.name === "Acertos")?.value ?? 0;
  const er = payload.find(p => p.name === "Erros")?.value ?? 0;
  const tot = ac + er;
  const pct = tot > 0 ? Math.round((ac / tot) * 100) : 0;
  return (
    <div className="bg-surface border border-edge rounded-lg p-3 text-xs shadow-sm min-w-[155px]">
      <p className="font-medium text-ink mb-2 truncate max-w-[180px]">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex justify-between gap-5">
          <span style={{ color: p.color as string }}>{p.name}</span>
          <span className="font-medium text-ink">{p.value}</span>
        </div>
      ))}
      <div className="mt-2 pt-2 border-t border-edge flex justify-between">
        <span className="text-ink-muted">Taxa</span>
        <span
          className={`font-semibold ${pct >= 60 ? "" : "text-danger"}`}
          style={pct >= 60 ? { color: C.greenDk } : undefined}
        >
          {pct}%
        </span>
      </div>
    </div>
  );
}

function MockTip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload as MockStat;
  return (
    <div className="bg-surface border border-edge rounded-lg p-3 text-xs shadow-sm min-w-[170px]">
      <p className="font-medium text-ink mb-2 leading-tight">{d.fullName}</p>
      <div className="flex justify-between gap-5">
        <span style={{ color: C.green }}>Acertos</span>
        <span className="font-medium text-ink">
          {d.correct}{d.total > 0 ? ` / ${d.total}` : ""}
        </span>
      </div>
      {d.total > 0 && (
        <div className="flex justify-between gap-5 mt-1">
          <span className="text-ink-muted">Taxa</span>
          <span
            className={`font-semibold ${d.pct >= 60 ? "" : "text-danger"}`}
            style={d.pct >= 60 ? { color: C.greenDk } : undefined}
          >
            {d.pct}%
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Heatmap ──────────────────────────────────────────────────────────────────
function heatColor(count: number, isFuture: boolean): string {
  if (isFuture) return "transparent";
  if (count === 0)  return cssVar("--c-surface-muted");
  if (count <= 3)   return C.heat1;
  if (count <= 8)   return C.heat2;
  if (count <= 15)  return C.heat3;
  return C.heat4;
}

/** Counts keyed "yyyy-MM-dd", built from resolutions' resolvedAt. */
function countsByDay(resolucoes: any[]): Record<string, number> {
  const actMap: Record<string, number> = {};
  resolucoes.forEach(r => {
    if (!r.resolvedAt) return;
    try {
      const key = format(parseISO(r.resolvedAt), "yyyy-MM-dd");
      actMap[key] = (actMap[key] ?? 0) + 1;
    } catch { /* skip */ }
  });
  return actMap;
}

function ActivityHeatmap({
  actMap, weeks: WEEKS = 14, unitLabel,
}: {
  /** day ("yyyy-MM-dd") → count */
  actMap: Record<string, number>;
  weeks?: number;
  unitLabel: (n: number) => string;
}) {
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 0 });
  const startDate = addDays(subWeeks(weekStart, WEEKS - 1), 0);

  const weeks: HeatCell[][] = [];
  for (let w = 0; w < WEEKS; w++) {
    const week: HeatCell[] = [];
    for (let d = 0; d < 7; d++) {
      const date = addDays(startDate, w * 7 + d);
      const key = format(date, "yyyy-MM-dd");
      week.push({ date, count: actMap[key] ?? 0, isFuture: date > today });
    }
    weeks.push(week);
  }

  const monthLabel = (wi: number) => {
    const first = weeks[wi][0].date;
    if (wi === 0) return format(first, "MMM", { locale: ptBR });
    const prev = weeks[wi - 1][0].date;
    return first.getMonth() !== prev.getMonth()
      ? format(first, "MMM", { locale: ptBR })
      : "";
  };

  const DAY_ABBR = ["D", "", "T", "", "Q", "", "S"];

  return (
    <div className="overflow-x-auto pb-1">
      <div className="flex gap-[3px]" style={{ minWidth: "fit-content" }}>
        <div className="flex flex-col gap-[3px] mr-1 pt-5">
          {DAY_ABBR.map((d, i) => (
            <div key={i} style={{ width: 12, height: 12, lineHeight: "12px" }}
              className="text-[9px] text-ink-muted text-right">
              {d}
            </div>
          ))}
        </div>
        {weeks.map((week, wi) => (
          <div key={wi} className="flex flex-col gap-[3px]">
            <div className="text-[9px] text-ink-muted h-4 capitalize">
              {monthLabel(wi)}
            </div>
            {week.map((cell, di) => (
              <div
                key={di}
                title={
                  cell.isFuture
                    ? ""
                    : `${format(cell.date, "dd/MM/yyyy")} — ${unitLabel(cell.count)}`
                }
                style={{
                  width: 12, height: 12,
                  background: heatColor(cell.count, cell.isFuture),
                  borderRadius: 2,
                  transition: "background 0.15s",
                }}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <span className="text-[10px] text-ink-muted">Menos</span>
        {[heatColor(0, false), C.heat1, C.heat2, C.heat3, C.heat4].map((c, i) => (
          <div key={i} style={{ width: 12, height: 12, background: c, borderRadius: 2 }} />
        ))}
        <span className="text-[10px] text-ink-muted">Mais</span>
      </div>
    </div>
  );
}

// ─── Tag heatmap ──────────────────────────────────────────────────────────────
function tagHeatColor(count: number, max: number): string {
  const r = max > 0 ? count / max : 0;
  if (r >= 0.8) return C.heat4;
  if (r >= 0.6) return C.heat3;
  if (r >= 0.3) return C.heat2;
  if (r >  0)   return C.heat1;
  return C.heat0;
}

function TagHeatmap({ data, loading }: { data: TagCount[]; loading: boolean }) {
  if (loading) return <div className="text-sm text-ink-muted">Carregando...</div>;
  if (!data.length) return (
    <div className="flex flex-col items-center justify-center h-28 gap-2 text-ink-muted text-sm">
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
      Responda questões com tags para ver a frequência por tema.
    </div>
  );

  const max = Math.max(...data.map(t => t.count), 1);

  /*
    A nuvem preenche o card, e o card acompanha o gráfico ao lado.

    A altura da fileira é ditada pelo irmão — o gráfico "Desempenho por tag",
    cujo `tagBarH` cresce com os dados. Este cartão se estica até lá e rola por
    dentro quando as tags passam disso.

    O `absolute inset-0` da nuvem não é enfeite: numa célula de grid, que se
    ESTICA para caber no conteúdo, `flex-1` sozinho resolve contra uma altura
    que a própria nuvem acabou de criar — medido, 48 tags levavam a célula a
    925px e nada rolava. Fora do fluxo, quem decide a altura é só o irmão.

    `minHeight` segura o outro extremo: com duas ou três tags o card não desaba
    numa faixa fina. E a legenda fica fora do scroller, senão some rolando
    justo quando há tags demais para ler sem rolar.
  */
  return (
    /*
      Altura do CARD, não um número fixo.

      Antes era `maxHeight: 220` com a legenda dentro do próprio scroller: as
      tags não usavam o espaço que o card tinha — ele acompanha o gráfico
      vizinho, cuja altura cresce com os dados (`tagBarH`) — e a legenda
      rolava junto, sumindo justo quando havia tags demais para ler sem rolar.

      Agora a coluna ocupa o card inteiro, só a nuvem de tags rola, e a
      legenda fica ancorada embaixo. O `minHeight` evita o outro extremo: com
      duas ou três tags o card não desaba numa faixa fina.
    */
    <div className="flex flex-1 flex-col" style={{ minHeight: 180 }}>
      {/* `relative` + `absolute inset-0`: a nuvem ocupa exatamente o espaço
          que sobra, sem poder empurrá-lo. */}
      <div className="relative flex-1 min-h-0">
        <div className="absolute inset-0 flex flex-wrap content-start gap-2 overflow-y-auto overscroll-contain pr-1">
        {data.map(({ tag, count }) => {
          const bg = tagHeatColor(count, max);
          const dark = bg === C.heat3 || bg === C.heat4;
          const veryLight = bg === C.heat0;
          return (
            <div
              key={tag}
              title={`${tag} — ${count} resposta${count !== 1 ? "s" : ""}`}
              className={`flex flex-col items-center rounded-lg px-3 py-2 cursor-default transition-transform hover:scale-105 ${
                veryLight ? "border border-edge-strong" : "border-none"
              }`}
              style={{ background: bg, minWidth: 68 }}
            >
              <span
                className="text-[11px] font-semibold text-center leading-tight break-words max-w-[96px]"
                style={{ color: dark ? C.heatFgOnDark : C.heatFg }}
              >
                {tag}
              </span>
              <span className="text-[10px] mt-1" style={{ color: dark ? C.heatFgOnDarkMuted : C.heatFgMuted }}>
                {count}×
              </span>
            </div>
          );
        })}
        </div>
      </div>
      {/* fora do scroller: a legenda explica as cores e tem de ficar à vista */}
      <div className="flex items-center gap-2 mt-3 shrink-0">
        <span className="text-[10px] text-ink-muted">Menos</span>
        {[heatColor(0, false), C.heat1, C.heat2, C.heat3, C.heat4].map((c, i) => (
          <div key={i} style={{ width: 12, height: 12, background: c, borderRadius: 2 }} />
        ))}
        <span className="text-[10px] text-ink-muted">Mais</span>
      </div>
    </div>
  );
}

// ─── Card shell — header strip style from cowork design ───────────────────────
function Card({
  title, subtitle, children, className = "",
}: {
  title: string; subtitle?: string; children: React.ReactNode; className?: string;
}) {
  return (
    <div
      className={`bg-surface rounded-card border border-edge overflow-hidden flex flex-col ${className}`}
    >
      <div className="flex items-center justify-between px-5 py-3 bg-surface-muted border-b border-edge shrink-0">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        {subtitle && <span className="text-xs text-ink-muted">{subtitle}</span>}
      </div>
      {/* `min-h-0` deixa o corpo encolher abaixo do conteúdo — sem ele um
          filho de flex usa `min-height: auto` e cresce em vez de rolar. */}
      <div className="p-5 flex min-h-0 flex-1 flex-col">
        {children}
      </div>
    </div>
  );
}

// ─── KPI chip — pill style from cowork design ─────────────────────────────────
type KpiVariant = "neutral" | "green" | "amber" | "red";

function Kpi({
  label, value, sub, variant = "neutral", bar,
}: {
  label: string; value: string | number; sub?: string; variant?: KpiVariant; bar?: number;
}) {
  const styles: Record<KpiVariant, { wrap: string; val: string; sub: string; bar: string }> = {
    neutral: { wrap: "bg-surface-muted border-edge",  val: "text-ink",  sub: "text-ink-muted", bar: "bg-ink-muted"  },
    green:   { wrap: "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800",  val: "text-green-800 dark:text-green-300",  sub: "text-green-600 dark:text-green-400", bar: "bg-ok"  },
    amber:   { wrap: "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800",  val: "text-amber-800 dark:text-amber-300",  sub: "text-amber-600 dark:text-amber-400", bar: "bg-warn"  },
    red:     { wrap: "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800",          val: "text-red-700 dark:text-red-300",      sub: "text-red-400 dark:text-red-400",      bar: "bg-danger"      },
  };
  const s = styles[variant];
  const barPct = bar === undefined ? undefined : Math.min(100, Math.max(0, bar));
  return (
    <div className={`rounded-card px-5 py-4 border ${s.wrap}`}>
      <p className="text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-2">{label}</p>
      <p className={`font-bold leading-tight break-words ${String(value).length > 16 ? "text-base" : "text-2xl"} ${s.val}`}>{value}</p>
      {sub && <p className={`text-xs mt-1.5 ${s.sub}`}>{sub}</p>}
      {barPct !== undefined && (
        <div className="mt-2 h-1.5 w-full rounded-full bg-surface overflow-hidden" role="progressbar" aria-valuenow={barPct} aria-valuemin={0} aria-valuemax={100}>
          <div className={`h-full rounded-full ${s.bar}`} style={{ width: `${barPct}%` }} />
        </div>
      )}
    </div>
  );
}

// ─── Chart legend row ─────────────────────────────────────────────────────────
function LegRow({ items }: { items: { color: string; label: string }[] }) {
  return (
    <div className="flex gap-4 mb-3">
      {items.map(it => (
        <span key={it.label} className="flex items-center gap-1.5 text-xs text-ink-muted">
          <span style={{ background: it.color }} className="inline-block w-2.5 h-2.5 rounded-sm" />
          {it.label}
        </span>
      ))}
    </div>
  );
}

// ─── Donut read-out row — colour dot + label + value ──────────────────────────
function DonutStat({
  color, label, value, strong,
}: { color?: string; label: string; value: number; strong?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {color
        ? <span style={{ background: color }} className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" />
        : <span className="inline-block w-2.5 h-2.5 shrink-0" />}
      <span className="text-xs text-ink-muted">{label}</span>
      <span className={`ml-auto text-sm text-ink ${strong ? "font-bold" : "font-semibold"}`}>
        {value.toLocaleString("pt-BR")}
      </span>
    </div>
  );
}

// ─── Home ─────────────────────────────────────────────────────────────────────
export default function Home() {
  usePageTitle("Dashboard");
  const navigate = useNavigate();
  const { buscarPaginada } = useResolucaoQuestoes();

  const [subjectStats, setSubjectStats] = useState<Materia.SubjectStats[]>([]);
  const [tagStats,     setTagStats]     = useState<TagStat[]>([]);
  const [tagCounts,    setTagCounts]    = useState<TagCount[]>([]);
  const [mockStats,    setMockStats]    = useState<MockStat[]>([]);
  const [resolucoes,   setResolucoes]   = useState<any[]>([]);
  const nextSimulado = useNextSimulado();
  const { usuario } = useUsuario();
  const dueCount = useDueCount();
  /* `lg` do Tailwind. É a MESMA largura em que a linha principal vira grade de
     3 colunas e o card do gráfico sai do fluxo — então é ela que decide se o
     gráfico tem uma altura definida para preencher (`100%`) ou precisa da sua
     própria (`subBarH`). Com `100%` abaixo de `lg` o gráfico simplesmente não
     renderizava: fora da grade o card tem altura automática, `100%` resolve
     para zero e o Recharts não desenha nada — zero barras, medido ao vivo. */
  const isLg = useMediaQuery("(min-width: 1024px)");
  const [reviewStats,  setReviewStats]  = useState<ReviewStats | null>(null);
  const [loadingReview, setLoadingReview] = useState(true);
  const [loading,      setLoading]      = useState(true);
  const [loadingTags,  setLoadingTags]  = useState(true);
  const [training,     setTraining]     = useState(false);
  const [isDark,       setIsDark]       = useState(() => document.documentElement.classList.contains("dark"));

  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains("dark"))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    ReviewService.getStats()
      .then(res => setReviewStats(res ?? null))
      .catch(() => setReviewStats(null))
      .finally(() => setLoadingReview(false));
  }, []);

  useEffect(() => {
    MateriaService.getEstatisticasMaterias()
      .then(res => { if (res) setSubjectStats(res); })
      .finally(() => setLoading(false));

    ResolucaoService.getResolucoes().then(res => {
      if (!res) return;
      setResolucoes(res);
      const map: Record<string, { correct: number; wrong: number }> = {};
      res.forEach(r => {
        const raw: string = (r.question as any)?.keyword ?? "";
        const tags = raw.split(",").map((t: string) => t.trim()).filter(Boolean);
        if (!tags.length || r.correct === undefined) return;
        tags.forEach(tag => {
          if (!map[tag]) map[tag] = { correct: 0, wrong: 0 };
          if (r.correct) map[tag].correct++; else map[tag].wrong++;
        });
      });
      setTagStats(
        Object.entries(map)
          .map(([tag, v]) => ({ tag, ...v }))
          .filter(s => s.correct + s.wrong >= 2)
          .sort((a, b) => b.correct + b.wrong - (a.correct + a.wrong))
          .slice(0, 12)
      );
    });

    ResolucaoService.getTagCounts()
      .then(res => { if (res) setTagCounts(res); })
      .finally(() => setLoadingTags(false));

    InscritoService.getLastFinished().then(res => {
      if (!res) return;
      const sorted = [...res].sort((a: any, b: any) =>
        new Date(a.endDate ?? a.registryDate ?? 0).getTime() -
        new Date(b.endDate ?? b.registryDate ?? 0).getTime()
      );
      setMockStats(
        sorted
          .filter((i: any) => (i.totalQuestions ?? 0) > 0 || (i.totalCorrect ?? 0) > 0)
          .map((i: any, idx: number) => {
            const qTotal: number  = i.totalQuestions ?? 0;
            const correct: number = i.totalCorrect   ?? 0;
            const pct = qTotal > 0 ? Math.round((correct / qTotal) * 100) : 0;
            const dateStr: string = i.endDate ?? i.registryDate ?? "";
            let label = `#${idx + 1}`;
            try { label = format(parseISO(dateStr), "dd/MM/yy"); } catch { /* keep fallback */ }
            return {
              name: label,
              fullName: i.description ?? `Simulado ${idx + 1}`,
              label, correct, total: qTotal, pct,
            };
          })
      );
    });
  }, []);

  const treinar = async () => {
    setTraining(true);
    try {
      await buscarPaginada();
      navigate("/carrossel");
    } finally {
      setTraining(false);
    }
  };

  // ── KPIs ────────────────────────────────────────────────────────────────────
  const totalQ  = subjectStats.reduce((s, x) => s + (x.total ?? 0), 0);
  const totalAc = subjectStats.reduce((s, x) => s + (x.correct ?? 0), 0);
  const taxaAc  = totalQ > 0 ? Math.round((totalAc / totalQ) * 100) : 0;

  // Visão geral's donut reads these same totals — never recomputed from a
  // different source, so the card and the "Taxa de acerto" KPI can't disagree.
  const totalErr    = totalQ - totalAc;
  const hasAnswers  = totalQ > 0;
  const donutData   = [
    { name: "Acertos", value: totalAc },
    { name: "Erros",   value: totalErr },
  ];

  const activeSubjectStats = subjectStats.filter(s => (s.correct ?? 0) + (s.wrong ?? 0) > 0);

  const sortedByPct = [...activeSubjectStats].sort((a, b) => {
    const pA = a.total ? (a.correct ?? 0) / a.total : 0;
    const pB = b.total ? (b.correct ?? 0) / b.total : 0;
    return pB - pA;
  });
  const melhor = sortedByPct[0]?.subject ?? "—";
  const pior   = sortedByPct[sortedByPct.length - 1]?.subject ?? "—";

  const subjectChartData = activeSubjectStats.map(s => ({
    ...s,
    shortName:
      s.subject && s.subject.length > 18
        ? s.subject.substring(0, 18) + "…"
        : (s.subject ?? ""),
  }));

  const subBarH = Math.max(240, activeSubjectStats.length * 46);
  const tagBarH = Math.max(200, tagStats.length * 38);

  const streak      = calcCurrentStreak(resolucoes);
  const bestStreak  = calcBestStreak(resolucoes);
  const weekCutoff  = new Date(Date.now() - 7 * 86_400_000);
  const weeklyCount = resolucoes.filter(r => {
    try { return parseISO(r.resolvedAt) >= weekCutoff; } catch { return false; }
  }).length;
  const weeklyPct = Math.min(Math.round((weeklyCount / WEEKLY_GOAL) * 100), 100);
  const { text: simCountdown, urgent: simUrgent } = daysUntil(nextSimulado?.start);

  // Same fallback Sidebar.tsx's SidebarUserCard uses, so the two never show
  // different names for the same signed-in user.
  const userName = usuario?.userName || usuario?.fullName || "Usuário";
  const greetingDate = format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR });
  // dueCount is the same useDueCount() the Topbar review pill reads — the
  // shared store dedupes, so this mount adds no GET /review/stats.
  const dueGreeting =
    dueCount > 0
      ? `${dueCount.toLocaleString("pt-BR")} ${dueCount === 1 ? "cartão" : "cartões"} aguardando revisão`
      : "Nenhum cartão aguardando revisão";

  const lineKey: "pct" | "correct" = mockStats.some(m => m.total > 0) ? "pct" : "correct";
  const lineLabel = lineKey === "pct" ? "Taxa de acerto (%)" : "Questões corretas";

  return (
    <DefaultLayout hideFilter>
      <div className="min-h-full bg-canvas p-6 space-y-5">

        <CampaignModal surface="home" />

        {/* ── Page header: greeting (leading) + active filter chips (trailing) ── */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <p className="text-sm text-ink-muted">{greetingDate}</p>
            <h1 className="text-2xl font-bold text-ink">Olá, {userName}!</h1>
            <p className="mt-0.5 text-sm text-ink-secondary">{dueGreeting}</p>
          </div>
          {/* min-w-0, not shrink-0: with several filters active the strip must be
              free to shrink and let its own chips wrap, or this row would push
              wider than the viewport instead of staying on one line. */}
          <FilterChipStrip className="min-w-0" />
        </div>

        {/* ── KPI chips ───────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Questões respondidas" value={totalQ.toLocaleString("pt-BR")} sub="no total"                  variant="neutral" />
          <Kpi label="Taxa de acerto"        value={`${taxaAc}%`}                  sub={`${totalAc.toLocaleString("pt-BR")} acertos`} variant="green"   bar={taxaAc} />
          <Kpi label="Melhor matéria"        value={melhor}                         sub="maior % de acerto"        variant="green"   />
          <Kpi label="Precisa melhorar"      value={pior}                           sub="menor % de acerto"        variant="amber"   />
        </div>

        {/* ── Main row: bar chart + right column ──────────────────────────
            A partir de `lg` isto é uma grade de 3 colunas × 3 linhas, e a
            coluna da direita deixou de ser um `flex flex-col`: os três itens
            dela são itens da própria grade, presos à coluna 3 (`lg:col-start-3`)
            nas linhas 1, 2 e 3. É o que permite ao card do gráfico cobrir as
            linhas 1–2 (`lg:row-span-2`) e terminar exatamente onde o mini-mapa
            termina, em vez de esticar até a base do donut.

            O alinhamento é estrutural, não um número mágico: quem define a
            altura é a própria linha 2 (o mini-mapa), então mudar o tamanho do
            mapa realinha o gráfico sozinho.

            Abaixo de `lg` nada disso vale — a grade é de 1 coluna e os quatro
            itens empilham na ordem do DOM, igual a antes. */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">

          {/* Desempenho por matéria — bar chart kept exactly.
              A partir de `lg` o card é `absolute inset-0` dentro deste invólucro,
              e isso é o que faz o alinhamento valer SEMPRE. Spanando as linhas
              1–2 como item normal, o card ainda era conteúdo e o
              `minHeight: subBarH` do gráfico esticava a linha: com 12 matérias
              ele terminava 35px abaixo do mapa (medido). Fora do fluxo, quem
              dimensiona as linhas 1–2 é só a coluna da direita — o invólucro
              recebe essa altura e o card a preenche exatamente, rolando por
              dentro quando as matérias não couberem.
              Abaixo de `lg` nada é absoluto: o card volta ao fluxo normal. */}
          <div className="lg:relative lg:col-span-2 lg:row-span-2">
          <Card title="Desempenho por matéria" subtitle="acertos vs erros" className="lg:absolute lg:inset-0">
            <LegRow items={[{ color: C.green, label: "Acertos" }, { color: C.purple, label: "Erros" }]} />
            {/* Dois níveis de propósito. O externo é a área disponível do card
                (`flex-1` + `min-h-0`) e rola quando não cabe; o interno é a
                altura do gráfico: em `lg`, `h-full` + `height="100%"` o fazem
                preencher o espaço sobrando — era aí que ficava o vazio — e
                `minHeight: subBarH` preserva os ~46px por matéria quando há
                matérias demais, caso em que o externo rola em vez de espremer
                as barras. Abaixo de `lg` os dois voltam a `subBarH`, porque ali
                o card não tem altura definida e `100%` renderizaria um gráfico
                de altura zero (ver `isLg`). */}
            <div className="min-h-0 flex-1 overflow-y-auto">
            <div className={isLg ? "h-full" : undefined} style={{ minHeight: subBarH }}>
            <ResponsiveContainer width="100%" height={isLg ? "100%" : subBarH}>
              <BarChart
                layout="vertical"
                data={subjectChartData}
                margin={{ top: 0, right: 48, left: 0, bottom: 0 }}
                barSize={13}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={cssVar("--c-border")} />
                <XAxis type="number" tick={{ fontSize: 10, fill: cssVar("--c-text-muted") }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="shortName" width={126}
                  tick={{ fontSize: 11, fill: cssVar("--c-text-muted") }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTip />} cursor={{ fill: cssVar("--c-surface-hover") }} />
                <Bar dataKey="correct" name="Acertos" stackId="a" fill={C.green} />
                <Bar dataKey="wrong"   name="Erros"   stackId="a" fill={C.purple} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
            </div>
            </div>
          </Card>
          </div>

            {/* Treinar CTA — background is the --c-cta-from/--c-cta-to gradient,
                a dark navy/green panel in both themes (see index.css), so label/
                hint ink is the theme-invariant --c-cta-ink pair measured against
                it, not the page's --c-text ramp: white 10.95–16.97:1, muted
                7.13–11.06:1, brand green (hint) 5.15–7.98:1 — all four gradient
                stops × both themes, all clear of the 4.5:1 floor. */}
            <button
              onClick={treinar}
              disabled={training}
              className="group relative rounded-card w-full overflow-hidden border border-brand/35 cursor-pointer lg:col-start-3 lg:row-start-1 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg active:scale-[.98] active:translate-y-0 disabled:cursor-not-allowed"
              style={{
                background: `linear-gradient(160deg, rgb(var(--c-cta-from)) 0%, rgb(var(--c-cta-to)) 100%)`,
                boxShadow: `0 2px 16px 0 rgb(var(--c-primary) / 0.18), 0 1px 3px 0 rgba(0,0,0,0.04)`,
              }}
            >
              <span className="relative flex flex-col items-center justify-center gap-3 py-7 px-5">

                {/* icon — the frame's solid brand-green button */}
                <span className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 bg-brand">
                  {training ? (
                    <svg className="animate-spin" width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="rgb(var(--c-primary-contrast))" strokeWidth="2" strokeOpacity="0.3" />
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="rgb(var(--c-primary-contrast))" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="rgb(var(--c-primary-contrast))"
                      className="transition-transform duration-300 group-hover:translate-x-0.5">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                  )}
                </span>

                {/* labels */}
                <span className="flex flex-col items-center gap-0.5">
                  <span className="font-semibold text-base" style={{ color: "rgb(var(--c-cta-ink))" }}>
                    {training ? "Carregando…" : "Treinar agora"}
                  </span>
                  <span className="text-xs" style={{ color: "rgb(var(--c-cta-ink-muted))" }}>
                    Questões aleatórias
                  </span>
                </span>

                {/* cta hint — resting state must itself clear 4.5:1, so hover is
                    gap/arrow motion only, not an opacity fade */}
                {!training && (
                  <span className="flex items-center gap-1 text-[11px] font-medium transition-all duration-200 group-hover:gap-1.5 text-brand">
                    Começar
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round"
                      className="transition-transform duration-200 group-hover:translate-x-0.5">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                )}
              </span>
            </button>

            {/* ── Cidade 3D (mini-mapa) ───────────────────────────────────
                Logo abaixo do "Treinar agora", na mesma coluna. Quadrado e
                pequeno em repouso; cresce no hover por cima do conteúdo à
                esquerda, sem mexer no layout (ver Dashboard3DPanel).

                A seção NÃO entra mais acima dos ladrilhos de métrica, então a
                ordem de seções exigida por
                `openspec/changes/app-shell-pencil-palette-and-home-order/specs/home-dashboard/spec.md`
                volta a valer intacta: cabeçalho → ladrilhos → matéria/CTA. O
                mini-mapa vive DENTRO da terceira seção, como peça da coluna do
                CTA, não como seção própria — por isso não há mais desvio de
                spec a emendar. */}
            <Dashboard3DPanel className="lg:col-start-3 lg:row-start-2" />
        </div>

        {/* ── Progress cards ───────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Meta semanal */}
          <Card title="Meta da semana" subtitle={`${weeklyCount} / ${WEEKLY_GOAL} questões`}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-ink-muted">respondidas esta semana</p>
              <span
                className="text-2xl font-bold"
                style={{ color: weeklyPct >= 100 ? C.greenDk : weeklyPct >= 60 ? C.green : C.purple }}
              >
                {weeklyPct}%
              </span>
            </div>
            <div className="w-full h-3 rounded-full overflow-hidden bg-surface-muted">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${weeklyPct}%`,
                  background:
                    weeklyPct >= 100 ? C.greenDk :
                    weeklyPct >= 60  ? C.green   : C.purple,
                }}
              />
            </div>
            <div className="flex justify-between mt-2">
              {[25, 50, 75, 100].map(m => (
                <span
                  key={m}
                  className={`text-[10px] ${weeklyPct >= m ? "" : "text-ink-muted"}`}
                  style={weeklyPct >= m ? { color: C.greenDk } : undefined}
                >
                  {m === 100 ? "🎯 Meta" : `${m}%`}
                </span>
              ))}
            </div>
          </Card>

          {/* Sequência */}
          <Card title="Sequência de estudos">
            <div className="flex items-end gap-3 mb-5">
              <span className="text-5xl font-bold text-ink leading-none">{streak}</span>
              <div className="pb-1">
                <p className="text-sm font-medium text-ink-secondary">
                  {streak === 1 ? "dia" : "dias"} seguidos
                </p>
                <p className="text-xs text-ink-muted">
                  🔥 Recorde: {bestStreak} {bestStreak === 1 ? "dia" : "dias"}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {buildWeekStrip(resolucoes).map(day => (
                <div key={day.key} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className={`w-full rounded-lg border ${
                      day.active ? "bg-ok border-ok" : "bg-surface-muted border-edge"
                    } ${day.isToday ? "ring-2 ring-select ring-offset-2 ring-offset-surface" : ""}`}
                    style={{ height: 28 }}
                    data-testid="week-strip-day"
                    data-active={day.active}
                    data-today={day.isToday}
                  />
                  <span className={`text-[9px] ${day.isToday ? "font-semibold text-ink" : "text-ink-muted"}`}>
                    {day.label}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* Próximo simulado */}
          <Card title="Próximo simulado">
            {nextSimulado ? (
              <div className="flex flex-col gap-3">
                <div>
                  <p className="text-base font-semibold text-ink leading-tight">
                    {nextSimulado.description}
                  </p>
                  {nextSimulado.start && (
                    <p className="text-xs text-ink-muted mt-1">
                      {format(parseISO(nextSimulado.start), "dd 'de' MMMM 'às' HH:mm", { locale: ptBR })}
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm font-medium ${simUrgent ? "text-danger" : ""}`}
                    style={simUrgent ? undefined : { color: C.purple }}
                  >
                    ⏰ {simCountdown}
                  </span>
                  <span
                    className={`text-xs font-medium px-3 py-1 rounded-full ${
                      nextSimulado.registered ? "" : "bg-accent-purple/[0.12] text-accent-purple-ink"
                    }`}
                    style={nextSimulado.registered ? { background: C.heat1, color: C.greenDk } : undefined}
                  >
                    {nextSimulado.registered ? "✓ Inscrito" : "Disponível"}
                  </span>
                </div>
                {!nextSimulado.registered && (
                  /*
                    Vai para a INSCRIÇÃO, não para `/listar-simulado`.

                    `/listar-simulado` é rota `ADMIN` (ver MyRoutes): o aluno
                    que clicava aqui caía no `<AccessDenied>` — justamente
                    quem o botão existe para atender. `/inscricao/:tipo` é a
                    tela em que ele se inscreve de fato.
                  */
                  <button
                    data-testid="home-inscrever-simulado"
                    onClick={() =>
                      navigate(`/inscricao/${nextSimulado.type ?? "completa"}`, {
                        state: nextSimulado.type ?? "completa",
                      })
                    }
                    className="w-full rounded-xl py-2 text-sm font-medium text-white transition-colors cursor-pointer border-0"
                    style={{ background: C.purple }}
                  >
                    Inscrever-se
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-20 gap-1 text-ink-muted">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <span className="text-sm">Nenhum simulado disponível</span>
              </div>
            )}
          </Card>
        </div>

        {/* ── Recent mock tests + study activity + overview ─────────────
            Três cards agora: "Visão geral" saiu da coluna do CTA (onde era a
            linha 3 da grade principal) para ficar ao lado de "Atividade de
            estudos". Com isso a linha principal passa a ter só duas linhas —
            gráfico | CTA + mini-mapa — e nada mais sobra embaixo dela. */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <Card title="Últimos simulados" subtitle={lineLabel}>
            {mockStats.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-28 gap-2 text-ink-muted text-sm">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8"  y1="2" x2="8"  y2="6" />
                  <line x1="3"  y1="10" x2="21" y2="10" />
                </svg>
                {loading ? "Carregando..." : "Nenhum simulado finalizado ainda."}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={mockStats} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={cssVar("--c-border")} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: cssVar("--c-text-muted") }} axisLine={false} tickLine={false} />
                  <YAxis
                    domain={lineKey === "pct" ? [0, 100] : ["auto", "auto"]}
                    tick={{ fontSize: 11, fill: cssVar("--c-text-muted") }}
                    axisLine={false} tickLine={false}
                    tickFormatter={v => lineKey === "pct" ? `${v}%` : String(v)}
                  />
                  <Tooltip content={<MockTip />} cursor={{ stroke: cssVar("--c-border-strong"), strokeWidth: 1 }} />
                  <Line
                    type="monotone"
                    dataKey={lineKey}
                    name={lineLabel}
                    stroke={C.green}
                    strokeWidth={2.5}
                    dot={<Dot r={5} fill={C.purple} stroke={C.dotRing} strokeWidth={2} />}
                    activeDot={{ r: 7, fill: C.green, stroke: C.dotRing, strokeWidth: 2 }}
                  />
                  {lineKey === "pct" && (
                    <line
                      x1="0%" x2="100%" y1="60%" y2="60%"
                      stroke={cssVar("--c-danger")} strokeDasharray="4 3" strokeWidth={1}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
          <Card title="Atividade de estudos" subtitle="últimas 14 semanas">
            {loading ? (
              <div className="text-sm text-ink-muted">Carregando...</div>
            ) : (
              <ActivityHeatmap
                actMap={countsByDay(resolucoes)}
                unitLabel={n => `${n} ${n === 1 ? "questão" : "questões"}`}
              />
            )}
          </Card>

          {/* Donut: acertos vs erros — same totals as the "Taxa de acerto" KPI above.
              Fills are C.greenDk / C.purple, not C.green: C.green measures 1.93:1 on
              a white card (documented for the forecast bars above), under the 3:1
              floor; greenDk clears 3.44:1 light / 4.80:1 dark, purple 3.31:1 light /
              4.99:1 dark — both against --c-surface (#FFFFFF light / #0D2033 dark). */}
          <Card title="Visão geral" subtitle="acertos vs erros">
            {hasAnswers ? (
              <div className="flex items-center gap-5">
                <PieChart width={140} height={140} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                  <Pie
                    data={donutData}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={66}
                    innerRadius={46}
                    startAngle={90}
                    endAngle={-270}
                    paddingAngle={2}
                    stroke="none"
                  >
                    <Cell fill={C.greenDk} />
                    <Cell fill={C.purple} />
                  </Pie>
                </PieChart>
                <div className="flex flex-1 flex-col gap-2.5">
                  <DonutStat color={C.greenDk} label="Acertos" value={totalAc} />
                  <DonutStat color={C.purple}  label="Erros"   value={totalErr} />
                  <div className="pt-2 border-t border-edge">
                    <DonutStat label="Total" value={totalQ} strong />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-1 py-10 text-center">
                <p className="text-sm font-medium text-ink-secondary">Nada para mostrar ainda</p>
                <p className="text-xs text-ink-muted">Responda questões para ver seus acertos e erros aqui.</p>
              </div>
            )}
          </Card>
        </div>

        {/* ── Revisão espaçada (R16) ───────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-ink">
              Revisão espaçada
            </h2>
            <button
              onClick={() => navigate("/revisao")}
              className="text-xs font-semibold text-ink-muted hover:text-ink"
            >
              Ir para a revisão →
            </button>
          </div>

          {loadingReview ? (
            <div className="text-sm text-ink-muted">Carregando...</div>
          ) : !reviewStats ? (
            <Card title="Revisão espaçada" subtitle="indisponível">
              <p className="text-sm text-ink-muted py-4 text-center">
                Não foi possível carregar suas estatísticas de revisão.
              </p>
            </Card>
          ) : (
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi
                  label="Vencem hoje"
                  value={reviewStats.dueToday}
                  variant={reviewStats.dueToday > 0 ? "amber" : "green"}
                  sub={reviewStats.dueToday > 0 ? "aguardando revisão" : "tudo em dia"}
                />
                <Kpi
                  label="Revisados hoje"
                  value={reviewStats.reviewedToday}
                  sub={reviewStats.reviewedToday === 1 ? "cartão" : "cartões"}
                />
                {/* retention already arrives 0–100 from the API, not 0–1 */}
                <Kpi
                  label="Retenção"
                  value={`${reviewStats.retention}%`}
                  variant={reviewStats.retention >= 80 ? "green" : reviewStats.retention >= 60 ? "amber" : "red"}
                  sub="últimos 30 dias"
                />
                <Kpi
                  label="Sequência"
                  value={reviewStats.streak}
                  sub={reviewStats.streak === 1 ? "dia seguido" : "dias seguidos"}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Card title="Previsão de revisões" subtitle="próximos 15 dias">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={(reviewStats.forecast ?? []).map(f => ({
                        ...f,
                        label: format(parseISO(f.day), "dd/MM"),
                      }))}
                      margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={cssVar("--c-border")} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 10, fill: cssVar("--c-text-muted") }}
                        interval={1}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 10, fill: cssVar("--c-text-muted") }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip content={<ChartTip />} cursor={{ fill: cssVar("--c-surface-hover", 0.5) }} />
                      {/* single series — the card title names it, so no legend */}
                      <Bar dataKey="count" name="Cartões" fill={FORECAST_BAR} radius={[4, 4, 0, 0]} maxBarSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>

                <Card title="Maturidade do baralho" subtitle="distribuição dos cartões">
                  <MaturityBar maturity={reviewStats.maturity} />
                </Card>
              </div>

              <Card title="Atividade de revisão" subtitle="últimas 8 semanas">
                <ActivityHeatmap
                  actMap={Object.fromEntries(
                    (reviewStats.heatmap ?? []).map(h => [h.day, h.count])
                  )}
                  weeks={8}
                  unitLabel={n => `${n} ${n === 1 ? "cartão" : "cartões"}`}
                />
              </Card>
            </div>
          )}
        </div>

        {/* ── Tag performance + tag frequency ───────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card title="Desempenho por tag" subtitle="palavras-chave mais respondidas">
            {tagStats.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-28 gap-2 text-ink-muted text-sm">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                  <line x1="7" y1="7" x2="7.01" y2="7" />
                </svg>
                {loading ? "Carregando..." : "Responda questões com tags para ver seu desempenho por tema."}
              </div>
            ) : (
              <>
                <LegRow items={[{ color: C.green, label: "Acertos" }, { color: C.purple, label: "Erros" }]} />
                <ResponsiveContainer width="100%" height={tagBarH}>
                  <BarChart
                    layout="vertical"
                    data={tagStats}
                    margin={{ top: 0, right: 48, left: 0, bottom: 0 }}
                    barSize={11}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={cssVar("--c-border")} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: cssVar("--c-text-muted") }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="tag" width={110}
                      tick={{ fontSize: 11, fill: cssVar("--c-text-muted") }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTip />} cursor={{ fill: cssVar("--c-surface-hover") }} />
                    <Bar dataKey="correct" name="Acertos" stackId="a" fill={C.green} />
                    <Bar dataKey="wrong"   name="Erros"   stackId="a" fill={C.purple} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </>
            )}
          </Card>
          <Card title="Frequência por tag" subtitle="questões respondidas por tema">
            <TagHeatmap data={tagCounts} loading={loadingTags} />
          </Card>
        </div>

      </div>
    </DefaultLayout>
  );
}
